import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { CheckboxField, SelectField, TextField } from '../../../components/ui/Field';
import { ErrorState, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { ApiError } from '../../../services/http';
import { tenantApi, tenantKeys, type Settings, type WorkingHours } from '../api';

const DAYS: (keyof WorkingHours)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<keyof WorkingHours, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};
const TIMEZONES =
  typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : ['Asia/Kolkata', 'UTC'];

type Section = 'all' | 'notifications';

/** Locale, working hours and quiet hours (FR-TEN-04, OD-23). Saves with an optimistic version check. */
export function PreferencesForm({ section = 'all', onSaved }: { section?: Section; onSaved?: () => void }) {
  const settings = useQuery({ queryKey: tenantKeys.settings, queryFn: tenantApi.settings });
  if (settings.isPending) return <TableSkeleton rows={4} />;
  if (settings.isError) return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  return (
    <PreferencesEditor
      key={settings.data.version ?? 0}
      initial={settings.data}
      section={section}
      reload={() => void settings.refetch()}
      {...(onSaved ? { onSaved } : {})}
    />
  );
}

function PreferencesEditor({
  initial,
  section,
  reload,
  onSaved,
}: {
  initial: Settings;
  section: Section;
  reload: () => void;
  onSaved?: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Settings>(initial);

  const save = useMutation({
    mutationFn: (d: Settings) => {
      const { version, ...rest } = d;
      const body =
        section === 'notifications'
          ? { quietHoursStart: rest.quietHoursStart ?? null, quietHoursEnd: rest.quietHoursEnd ?? null }
          : {
              timezone: rest.timezone,
              currency: rest.currency,
              dateFormat: rest.dateFormat,
              timeFormat: rest.timeFormat,
              weekStart: rest.weekStart,
              workingHours: rest.workingHours,
              quietHoursStart: rest.quietHoursStart ?? null,
              quietHoursEnd: rest.quietHoursEnd ?? null,
              transferRequiresApproval: rest.transferRequiresApproval,
            };
      return tenantApi.updateSettings({ ...body, expectedVersion: version ?? 1 });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(tenantKeys.settings, data);
      toast.success('Settings saved');
      onSaved?.();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'VERSION_CONFLICT') {
        toast.error(
          new Error(
            'Someone else changed these settings. The latest values were loaded; review and save again.',
          ),
        );
        reload();
      } else toast.error(err);
    },
  });

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const hours = draft.workingHours;

  return (
    <div className="space-y-6">
      {section === 'all' ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Time zone"
              value={draft.timezone}
              onChange={(e) => set('timezone', e.target.value)}
              options={TIMEZONES.map((z) => ({ value: z, label: z }))}
            />
            <TextField
              label="Currency"
              value={draft.currency}
              maxLength={3}
              onChange={(e) => set('currency', e.target.value.toUpperCase())}
            />
            <SelectField
              label="Date format"
              value={draft.dateFormat}
              onChange={(e) => set('dateFormat', e.target.value)}
              options={['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd', 'dd-MM-yyyy', 'dd MMM yyyy'].map((f) => ({
                value: f,
                label: f,
              }))}
            />
            <SelectField
              label="Time format"
              value={draft.timeFormat}
              onChange={(e) => set('timeFormat', e.target.value as '12h' | '24h')}
              options={[
                { value: '12h', label: '12-hour (2:30 PM)' },
                { value: '24h', label: '24-hour (14:30)' },
              ]}
            />
          </div>
          {hours ? (
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-fg">Working hours</legend>
              <div className="divide-y divide-line rounded-md border border-line">
                {DAYS.map((day) => {
                  const range = hours[day][0];
                  return (
                    <div key={day} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                      <label className="flex w-36 items-center gap-2">
                        <input
                          type="checkbox"
                          className="size-4 accent-brand-600"
                          checked={!!range}
                          onChange={(e) =>
                            set('workingHours', {
                              ...hours,
                              [day]: e.target.checked ? [['09:00', '19:00']] : [],
                            })
                          }
                        />
                        {DAY_LABELS[day]}
                      </label>
                      {range ? (
                        <span className="flex items-center gap-2">
                          <input
                            aria-label={`${DAY_LABELS[day]} opens`}
                            type="time"
                            value={range[0]}
                            onChange={(e) =>
                              set('workingHours', { ...hours, [day]: [[e.target.value, range[1]]] })
                            }
                            className="h-8 rounded-md border border-line bg-surface px-2"
                          />
                          to
                          <input
                            aria-label={`${DAY_LABELS[day]} closes`}
                            type="time"
                            value={range[1]}
                            onChange={(e) =>
                              set('workingHours', { ...hours, [day]: [[range[0], e.target.value]] })
                            }
                            className="h-8 rounded-md border border-line bg-surface px-2"
                          />
                        </span>
                      ) : (
                        <span className="text-fg-subtle">Closed</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ) : null}
        </>
      ) : null}
      <fieldset className="grid gap-4 sm:grid-cols-2">
        <legend className="mb-2 text-sm font-medium text-fg sm:col-span-2">Quiet hours</legend>
        <TextField
          label="From"
          type="time"
          value={draft.quietHoursStart ?? ''}
          onChange={(e) => set('quietHoursStart', e.target.value || null)}
          hint="Non-urgent staff notifications wait until quiet hours end"
        />
        <TextField
          label="Until"
          type="time"
          value={draft.quietHoursEnd ?? ''}
          onChange={(e) => set('quietHoursEnd', e.target.value || null)}
        />
      </fieldset>
      {section === 'all' ? (
        <CheckboxField
          label="Lead transfers need approval"
          description="A manager or team leader approves transfers before ownership changes."
          checked={!!draft.transferRequiresApproval}
          onChange={(e) => set('transferRequiresApproval', e.target.checked)}
        />
      ) : null}
      <Button onClick={() => save.mutate(draft)} loading={save.isPending}>
        Save settings
      </Button>
    </div>
  );
}
